import { ComponentProps, FC, useMemo } from 'react';
import { Button, Icon } from '@kontentino/ui';
import { faArrowsRotate } from '@fortawesome/pro-regular-svg-icons/faArrowsRotate';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown';
import { faLink } from '@fortawesome/pro-regular-svg-icons/faLink';
import { faPause } from '@fortawesome/pro-regular-svg-icons/faPause';
import { faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons/faTriangleExclamation';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import dayjs from 'dayjs';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import type { AnalyticsSyncButtonState } from 'app/modules/analytics/types/AnalyticsSyncButtonState';

type ButtonVariant = NonNullable<ComponentProps<typeof Button>['variant']>;

interface StateConfig {
  variant: ButtonVariant;
  icon: IconDefinition;
  iconClassName?: string;
  label: string;
  subtitle: string;
  className?: string;
}

interface Props {
  state: AnalyticsSyncButtonState;
  lastSyncedAt?: string | null;
  isSyncStatusPending?: boolean;
  trackingDisabledAt?: string | null;
  platformName?: string;
  isGroup?: boolean;
  onClick: () => void;
  disabled?: boolean;
  dataCy?: string;
  dataName?: string;
}

const STRIPED_SURFACE =
  'tw-border tw-border-grayscale-40 tw-bg-[repeating-linear-gradient(-45deg,#f4f4f6_0px,#f4f4f6_5px,#ffffff_5px,#ffffff_10px)]';

const AnalyticsSyncButton: FC<Props> = ({
  state,
  lastSyncedAt,
  isSyncStatusPending = false,
  trackingDisabledAt,
  platformName,
  isGroup = false,
  onClick,
  disabled,
  dataCy,
  dataName,
}) => {
  const { t } = useTranslation();

  const syncPausedSubtitle = useMemo(() => {
    if (state !== 'sync-paused') return '';
    if (trackingDisabledAt) {
      return t('analyticsSyncBtnTrackingOffSince', {
        date: dayjs(trackingDisabledAt).format('MMM D'),
      });
    }
    return t('analyticsSyncBtnTrackingOff');
  }, [state, trackingDisabledAt, t]);

  const config = useMemo((): StateConfig | null => {
    if (state === 'sync-paused') return null;
    const updatedSubtitle = isSyncStatusPending
      ? t('analyticsSyncBtnStatusChecking')
      : lastSyncedAt
      ? t('analyticsSyncBtnUpdated', { time: dayjs(lastSyncedAt).fromNow() })
      : t('analyticsSyncBtnNeverSynced');

    const stateConfigs: Record<
      Exclude<AnalyticsSyncButtonState, 'sync-paused'>,
      StateConfig
    > = {
      idle: {
        variant: 'secondary',
        icon: faArrowsRotate,
        label: t('analyticsSyncBtnLabel'),
        subtitle: updatedSubtitle,
      },
      syncing: {
        variant: 'primary',
        icon: faArrowsRotate,
        iconClassName: 'tw-animate-spin',
        label: t('analyticsSyncBtnSyncing'),
        subtitle: platformName
          ? t('analyticsSyncBtnPulling', { platform: platformName })
          : t('analyticsSyncBtnSyncing'),
      },
      success: {
        variant: 'success',
        icon: faCheck,
        label: t('analyticsSyncBtnUpToDate'),
        subtitle: t('analyticsSyncBtnJustSynced'),
      },
      error: {
        variant: 'danger',
        icon: faTriangleExclamation,
        label: t('analyticsSyncBtnSyncFailed'),
        subtitle: t('analyticsSyncBtnRetry'),
      },
      reconnect: {
        variant: 'danger',
        icon: faLink,
        label: t('analyticsSyncBtnReconnect'),
        subtitle: t('analyticsSyncBtnTokenExpired'),
      },
      'first-sync': {
        variant: 'primary',
        icon: faArrowsRotate,
        label: t('analyticsSyncBtnNow'),
        subtitle: t('analyticsSyncBtnNeverSynced'),
      },
    };

    const resolved = stateConfigs[state];

    // A group has no single "last synced" time, so drop the last-sync subtitle
    // and label the actionable states "Sync all".
    if (isGroup && (state === 'idle' || state === 'first-sync')) {
      return {
        ...resolved,
        label: t('analyticsSyncBtnSyncAll', { defaultValue: 'Sync all' }),
        subtitle: '',
      };
    }

    return resolved;
  }, [state, lastSyncedAt, isSyncStatusPending, platformName, isGroup, t]);

  if (state === 'sync-paused') {
    return (
      <button
        type="button"
        disabled
        className={clsx(
          'tw-flex tw-h-auto tw-min-h-[36px] tw-min-w-[220px] tw-cursor-not-allowed tw-items-center tw-justify-between tw-gap-2 tw-rounded-full tw-py-2 tw-pl-2 tw-pr-3 tw-text-left',
          STRIPED_SURFACE,
        )}
        data-cy={dataCy}
        data-name={dataName}
        aria-disabled
      >
        <span className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-2">
          <span className="tw-flex tw-shrink-0 tw-rounded-md tw-bg-grayscale-20 tw-p-2 tw-text-grayscale-120">
            <Icon icon={faPause} size="sm" />
          </span>
          <span className="tw-flex tw-min-w-0 tw-flex-col tw-gap-px">
            <span className="tw-text-xs tw-font-semibold tw-leading-tight tw-text-grayscale-180">
              {t('analyticsSyncBtnSyncPaused')}
            </span>
            <span className="tw-text-[10px] tw-leading-tight tw-text-grayscale-120">
              {syncPausedSubtitle}
            </span>
          </span>
        </span>
        <Icon
          icon={faChevronDown}
          size="sm"
          className="tw-flex-shrink-0 tw-text-grayscale-100"
          aria-hidden
        />
      </button>
    );
  }

  if (!config) return null;

  return (
    <Button
      variant={config.variant}
      size="small"
      className={clsx(
        'tw-h-auto tw-min-h-[36px] tw-items-center tw-py-1',
        config.className,
      )}
      iconBefore={
        <Icon icon={config.icon} className={config.iconClassName} size="sm" />
      }
      onClick={onClick}
      disabled={disabled}
      data-cy={dataCy}
      data-name={dataName}
    >
      <div className="tw-flex tw-flex-col tw-items-start tw-gap-px">
        <span className="tw-text-xs tw-font-semibold tw-leading-tight">
          {config.label}
        </span>
        {config.subtitle && (
          <span className="tw-text-[10px] tw-leading-tight tw-opacity-70">
            {config.subtitle}
          </span>
        )}
      </div>
    </Button>
  );
};

export default AnalyticsSyncButton;
